import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { createPopper, type Instance, type VirtualElement } from '@popperjs/core';
import sleep from '@/utils/sleep';
import scrollIntoView from 'scroll-into-view-if-needed';
import { Message } from '@patsnap/tahiti-core/common/message';
import type { DomNodesTipService } from '../dom-nodes-tip';

const TARGET_ANIMATION_CLASS = 'target-node-shine';

@injectable()
export class DomNodesTipServiceImpl implements DomNodesTipService {
    public nodesMap: Record<string, HTMLElement[]> = {};
    public currentIndexMap: Record<string, number> = {};
    public tipInfoMap: Record<string, {
        number: number,
        length: number,
    }> = {};

    private _wrapperMap: Record<string, HTMLElement> = {};
    private _tipMap: Record<string, HTMLElement> = {};
    private _popperMap: Record<string, Instance> = {};

   
    constructor(
        @inject(Message)
        private _message: Message,
    ) {}

    private _showTip(id: string) {
        this.tipInfoMap = {
            ...this.tipInfoMap,
            [id]: {
                number: this.currentIndexMap[id] + 1,
                length: this.nodesMap[id].length,
            },
        };
        this._tipMap[id]!.style.visibility = 'visible';
    }

    private async _jumpToTarget(targetEle: HTMLElement) {
        await sleep(200);
        if (targetEle.scrollIntoViewIfNeeded) {
            targetEle.scrollIntoViewIfNeeded();
        } else {
            scrollIntoView(targetEle, {
                scrollMode: 'if-needed',
                inline: 'end',
                block: 'nearest',
                behavior: 'smooth',
            });
        }
    }

    private async _positionTip(targetEle: HTMLElement, id: string) {
        this.closeTip(id);
        await this._jumpToTarget(targetEle);
        this._addTargetAnimation(targetEle);
        const target: VirtualElement = {
            getBoundingClientRect: (): DOMRect => {
                const rects = targetEle.getClientRects();
                const rect = targetEle.getBoundingClientRect();
                // 单行元素使用原始位置定位
                if (rects.length < 2) return rect;

                // 多行元素使用第一行的位置定位
                return new DOMRect(
                    rect.right - rects[0].width,
                    rect.top,
                    rects[0].width,
                    rects[0].height,
                );
            },
        };
        this._popperMap[id] = createPopper(target, this._tipMap[id]!, {
            placement: 'top',
            strategy: 'absolute',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8],
                    },
                },
            ],
        });

        this._showTip(id);
    }

    private _addTargetAnimation(targetEle: HTMLElement) {
        targetEle.classList.add(TARGET_ANIMATION_CLASS);
    }

    private _removeTargetAnimation(id: string) {
        (this.nodesMap[id] || []).forEach(node => {
            node.classList.remove(TARGET_ANIMATION_CLASS);
        });
    }

    public setTipRef(params: {
        ref: HTMLElement,
        id: string,
    }) {
        this._tipMap[params.id] = params.ref;
    }

    public setWrapperRef(params: {
        ref: HTMLElement,
        id: string,
    }) {
        this._wrapperMap[params.id] = params.ref;
    }

    public clearTip(id: string) {
        delete this._wrapperMap[id];
        delete this._tipMap[id];
        delete this._popperMap[id];
        delete this.currentIndexMap[id];
        delete this.tipInfoMap[id];
    }

    public initNodesTip({ selector, notFoundMessage }: { selector: string, notFoundMessage?: string }) {
        let _nodeIsNotFound = true;
        for (const [id, wrapperRef] of Object.entries(this._wrapperMap)) {
            this._removeTargetAnimation(id);
            // @ts-ignore
            this.nodesMap[id] = Array.from(wrapperRef!.getElementsByClassName(selector));
            this.currentIndexMap[id] = 0;
            const target = this.nodesMap[id][this.currentIndexMap[id]];
            if (!target || !this._tipMap[id]) {
                this.closeTip(id);
                continue;
            }
            _nodeIsNotFound = false;
            this._positionTip(target, id);
        }
        if (_nodeIsNotFound && notFoundMessage) {
            this._message.closeAll();
            this._message.warning({
                message: notFoundMessage,
                showClose: true,
            });
        }
    }

    public closeTip(id: string) {
        this._popperMap[id]?.destroy();
        this._removeTargetAnimation(id);
        this._tipMap[id]!.style.visibility = 'hidden';
    }

    private _processIndex(index: number, id: string) {
        if (index < 0) {
            return this.nodesMap[id].length - 1;
        }
        if (index >= this.nodesMap[id].length) {
            return index = 0;
        }
        return index;
    }

    public moveToTargetNode({ targetIdx, moveType, id }: {
        targetIdx?: number,
        moveType?: 'next' | 'prev',
        id: string,
    }) {
        let target: HTMLElement | undefined;
        if (targetIdx !== undefined) {
            target = this.nodesMap[id][targetIdx];
        } else if (moveType) {
            const delta = moveType === 'next' ? 1 : -1;
            const _index = this._processIndex(this.currentIndexMap[id] + delta, id);
            this.currentIndexMap[id] = _index;
            target = this.nodesMap[id][_index];
        }

        if (!target) return;
        this._positionTip(target, id);
    }
}
